/**
 * Server-side File Service - Upload files to the backend server
 *
 * Unlike fileStorageService (which uses IndexedDB), this service uploads
 * files to the Rust backend which proxies them to store.teknology.eu.
 * Files are accessible at https://store.teknology.eu/files/{id}.{ext}
 */

// =============================================================================
// TYPES
// =============================================================================

export interface UploadedFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url: string;
  fileRecordId?: string;
  scanStatus?: string;
}

export interface ServerFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  createdAt: string;
  url: string;
}

export interface FileListResponse {
  files: ServerFile[];
  total: number;
}

export interface UploadResponse {
  success: boolean;
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  url: string;
  file_record_id?: string;
  scan_status?: string;
  error?: string;
}

import { getGraphQLConfig } from '../database/graphqlClient';

// =============================================================================
// HELPERS
// =============================================================================

function getAuthHeaders(): HeadersInit {
  const { token } = getGraphQLConfig();
  return {
    'Authorization': `Bearer ${token}`,
  };
}

function getFullUrl(path: string): string {
  const { url } = getGraphQLConfig();
  const baseUrl = url.replace(/\/graphql$/, '');
  return `${baseUrl}${path}`;
}

// =============================================================================
// FILE OPERATIONS
// =============================================================================

/**
 * Upload a file to the server
 *
 * @param file - The file to upload (File or Blob)
 * @param filename - Optional custom filename
 * @returns The uploaded file info with server-side URL
 */
export async function uploadFile(
  file: File | Blob,
  filename?: string,
  purpose?: string
): Promise<UploadedFile> {
  const formData = new FormData();

  // If it's a Blob, we need to create a File with a name
  if (file instanceof Blob && !(file instanceof File)) {
    const name = filename || `file-${Date.now()}`;
    formData.append('file', file, name);
  } else {
    formData.append('file', file, filename || (file as File).name);
  }

  if (purpose) {
    formData.append('purpose', purpose);
  }

  const response = await fetch(getFullUrl('/upload'), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  const result: UploadResponse = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'Upload failed');
  }

  // The url may be absolute (https://store.teknology.eu/...) or relative (/files/...)
  const fileUrl = result.url.startsWith('http') ? result.url : getFullUrl(result.url);

  return {
    id: result.id,
    filename: result.filename,
    mimeType: result.mime_type,
    size: result.size,
    url: fileUrl,
    fileRecordId: result.file_record_id,
    scanStatus: result.scan_status,
  };
}

/**
 * Get a file's public URL by ID and extension
 *
 * @param id - The file UUID
 * @param extension - The file extension (e.g., 'pdf', 'jpg')
 * @returns The full public URL
 */
export function getFileUrl(id: string, extension: string): string {
  return `https://store.teknology.eu/files/${id}.${extension}`;
}

/**
 * Convert a file URL to use the public endpoint (no auth required)
 * Use this for avatar images and other public files that need to be displayed in <img> tags
 *
 * @param url - The original file URL (e.g., https://store.teknology.eu/files/uuid.jpg)
 * @returns The public URL (e.g., https://store.teknology.eu/public/uuid.jpg)
 */
export function getPublicFileUrl(url: string): string {
  if (!url) return url;
  // Convert /files/ URLs to /public/ for unauthenticated access
  return url.replace('store.teknology.eu/files/', 'store.teknology.eu/public/');
}

/**
 * List all uploaded files
 *
 * @param options - Optional filtering/pagination
 * @returns List of files with metadata
 */
export async function listFiles(options?: {
  mimeType?: string;
  limit?: number;
  offset?: number;
}): Promise<FileListResponse> {
  const params = new URLSearchParams();
  if (options?.mimeType) params.set('mime_type', options.mimeType);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());

  const url = getFullUrl('/files') + (params.toString() ? `?${params}` : '');

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`List files failed: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();

  // Map server response to our types
  return {
    files: result.files.map((f: any) => ({
      id: f.id,
      filename: f.filename,
      mimeType: f.mime_type,
      size: f.size,
      createdAt: f.created_at,
      url: getFullUrl(f.url),
    })),
    total: result.total,
  };
}

/**
 * Delete a file from the server
 *
 * @param id - The file UUID
 * @param extension - The file extension
 * @returns True if deleted successfully
 */
export async function deleteFile(id: string, extension: string): Promise<boolean> {
  const response = await fetch(getFullUrl(`/files/${id}.${extension}`), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result.success;
}

/**
 * Upload an audio recording to the server
 *
 * @param audioBlob - The audio blob from recording
 * @returns The uploaded file info
 */
export async function uploadAudioRecording(audioBlob: Blob): Promise<UploadedFile> {
  const filename = `recording-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
  return uploadFile(audioBlob, filename);
}

/**
 * Upload a document (PDF, Word, etc.) to the server
 *
 * @param file - The document file
 * @returns The uploaded file info
 */
export async function uploadDocument(file: File): Promise<UploadedFile> {
  return uploadFile(file);
}

/**
 * Upload an image to the server
 *
 * @param file - The image file
 * @returns The uploaded file info
 */
export async function uploadImage(file: File, purpose?: string): Promise<UploadedFile> {
  return uploadFile(file, undefined, purpose);
}

// =============================================================================
// EXPORT
// =============================================================================

export const serverFileService = {
  uploadFile,
  getFileUrl,
  listFiles,
  deleteFile,
  uploadAudioRecording,
  uploadDocument,
  uploadImage,
};

export default serverFileService;
